import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box, Button, Divider, Link, TextField, Typography,
  Alert, InputAdornment, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useLoginMutation } from '../../store/api/authApi';
import { loginSuccess } from '../../store/slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

const schema = yup.object({
  email: yup.string().email('Enter a valid email address').required('Email address is required'),
  password: yup.string().required('Password is required'),
});

type FormData = yup.InferType<typeof schema>;

// Component-scoped styles for login inputs to avoid any global CSS impact
const loginFieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(7, 19, 33, 0.65)',
    color: '#F8FAFC',
    fontSize: '0.9375rem',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(120, 180, 220, 0.3)',
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(120, 180, 220, 0.5)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(7, 19, 33, 0.9)',
      boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.25)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#38BDF8',
        borderWidth: '1px',
      },
    },
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#EF4444',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.25)',
      },
    },
    '& input': {
      color: '#F8FAFC',
    },
    '& input::placeholder': {
      color: '#94A3B8',
      opacity: 1,
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#EF4444',
    marginLeft: '4px',
    marginTop: '4px',
  },
};

export const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');

  const [login, { isLoading }] = useLoginMutation();

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      const result = await login(data).unwrap();
      localStorage.setItem('accessToken', result.access_token);
      localStorage.setItem('user', JSON.stringify(result.user));
      dispatch(loginSuccess({ user: result.user, accessToken: result.access_token }));
      navigate(result.user.installation_id ? '/dashboard' : '/onboarding/github-install', { replace: true });
    } catch (err: any) {
      setServerError(err?.data?.detail ?? 'Login failed. Please verify your credentials.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 0.75 }}>
        Welcome back
      </Typography>
      <Typography variant="body2" sx={{ color: '#94A3B8', textAlign: 'center', mb: 3.5 }}>
        Sign in to your KodeGauge account
      </Typography>

      {serverError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            bgcolor: 'rgba(239, 68, 68, 0.12)',
            color: '#EF4444',
            border: '1px solid rgba(239, 68, 68, 0.28)',
            borderRadius: '12px',
          }}
        >
          {serverError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Email Address */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Email address
          </Typography>
          <TextField
            {...register('email')}
            placeholder="you@company.com"
            type="email"
            fullWidth
            autoComplete="email"
            autoFocus
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={loginFieldSx}
          />
        </Box>

        {/* Password */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
              Password
            </Typography>
            <Link
              component={RouterLink}
              to="/auth/forgot-password"
              variant="caption"
              sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot password?
            </Link>
          </Box>
          <TextField
            {...register('password')}
            placeholder="••••••••"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            autoComplete="current-password"
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={loginFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? 'Hide password' : 'Show password'} arrow placement="top">
                    <IconButton
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      sx={{
                        width: 38,
                        height: 38,
                        color: '#94A3B8',
                        borderRadius: '8px',
                        transition: 'color 0.15s ease, background-color 0.15s ease',
                        '&:hover': {
                          color: '#F8FAFC',
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&:focus-visible': {
                          color: '#38BDF8',
                          backgroundColor: 'rgba(56, 189, 248, 0.12)',
                          outline: '2px solid #38BDF8',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Sign In Button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            mt: 3,
            mb: 2.5,
            py: 1.4,
            fontWeight: 700,
            fontSize: '0.95rem',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #39D6B0 0%, #20C878 100%)',
            color: '#071321',
            boxShadow: '0 2px 10px rgba(57, 214, 176, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4ce0bd 0%, #2bd987 100%)',
              boxShadow: '0 4px 16px rgba(57, 214, 176, 0.4)',
            },
          }}
        >
          {isLoading ? <CircularProgress size={22} sx={{ color: '#071321' }} /> : 'Sign in'}
        </Button>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: 'rgba(120, 180, 220, 0.14)' }}>
        <Typography variant="caption" sx={{ color: '#64748B', px: 1 }}>OR</Typography>
      </Divider>

      {/* GitHub OAuth Button */}
      <Button
        fullWidth
        variant="outlined"
        size="large"
        startIcon={<GitHubIcon sx={{ color: '#F8FAFC' }} />}
        onClick={() => { window.location.href = `${API_BASE_URL}/auth/github`; }}
        sx={{
          py: 1.25,
          fontSize: '0.9rem',
          fontWeight: 600,
          borderColor: 'rgba(120, 180, 220, 0.25)',
          color: '#F8FAFC',
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '10px',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(120, 180, 220, 0.4)',
          },
        }}
      >
        Continue with GitHub
      </Button>

      <Typography variant="body2" textAlign="center" sx={{ mt: 3.5, color: '#94A3B8' }}>
        Don't have an account?{' '}
        <Link
          component={RouterLink}
          to="/signup"
          sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Sign up
        </Link>
      </Typography>
    </Box>
  );
};

export default LoginPage;
